import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AlbumGrid from '../components/AlbumGrid';
import CreateAlbumModal from '../components/CreateAlbumModal';
import EditAlbumModal from '../components/EditAlbumModal';
import PinModal from '../components/PinModal';
import SignInButton from '../components/SignInButton';
import { useAuth } from '../contexts/AuthContext';
import { getAlbums, createAlbum, deleteAlbum, updateAlbumDetails } from '../services/albumService';
import { getSiteConfig } from '../services/firestoreService';
import { isPinCookieValid, setPinCookie } from '../utils/cookieUtils';
import { VIDEO_PIN } from '../constants/config';

const HomePage = () => {
  const navigate = useNavigate();
  const { isAuthorized, user } = useAuth();
  const [albums, setAlbums] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [isPinModalOpen, setIsPinModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [siteConfig, setSiteConfig] = useState({ title: 'CHS Media', description: '', showVideosButton: true });

  useEffect(() => {
    loadAlbums();
    loadSiteConfig();
  }, [user]); // Reload albums when user changes

  const loadSiteConfig = async () => {
    try {
      const config = await getSiteConfig();
      setSiteConfig(config);
      // Update document title
      document.title = config.title || 'CHS Media';
    } catch (error) {
      console.error('Error loading site config:', error);
    }
  };

  const loadAlbums = async () => {
    try {
      // Pass user email to filter restricted albums
      const albumList = await getAlbums(user?.email);
      setAlbums(albumList);
    } catch (error) {
      console.error('Error loading albums:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAlbum = async (metadata) => {
    try {
      await createAlbum(metadata.title, metadata);
      loadAlbums();
    } catch (error) {
      console.error('Error creating album:', error);
      alert('Error creating album. Please try again.');
    }
  };

  const handleDeleteAlbum = async (albumId) => {
    try {
      await deleteAlbum(albumId);
      loadAlbums();
    } catch (error) {
      console.error('Error deleting album:', error);
      alert('Error deleting album. Please try again.');
    }
  };

  const handleEditAlbum = (album) => {
    setEditingAlbum(album);
    setIsEditModalOpen(true);
  };

  const handleUpdateAlbum = async (updatedData) => {
    try {
      await updateAlbumDetails(editingAlbum.id, updatedData);
      loadAlbums();
      setIsEditModalOpen(false);
      setEditingAlbum(null);
    } catch (error) {
      console.error('Error updating album:', error);
      alert('Error updating album. Please try again.');
    }
  };

  const handleVideosClick = async () => {
    if (await isPinCookieValid(VIDEO_PIN)) {
      // Cookie is valid, go directly to videos
      navigate('/videos');
    } else {
      // No valid cookie, show PIN modal
      setIsPinModalOpen(true);
    }
  };

  const handlePinValidated = async (pin) => {
    await setPinCookie(pin);
    navigate('/videos');
  };

  if (loading) {
    return (
      <div className="w-full p-8">
        <div className="text-center py-12 text-text-dark text-lg">Loading albums...</div>
      </div>
    );
  }

  // Group albums by category and sort by date descending
  const groupedAlbums = albums.reduce((groups, album) => {
    const category = album.category || 'Uncategorized';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(album);
    return groups;
  }, {});

  // Sort albums within each category by date descending
  Object.keys(groupedAlbums).forEach(category => {
    groupedAlbums[category].sort((a, b) => new Date(b.date) - new Date(a.date));
  });

  // Sort categories by the youngest (most recent) album date in each category, descending
  const sortedCategories = Object.keys(groupedAlbums).sort((a, b) => {
    const aNewestDate = new Date(Math.max(...groupedAlbums[a].map(album => new Date(album.date))));
    const bNewestDate = new Date(Math.max(...groupedAlbums[b].map(album => new Date(album.date))));
    return bNewestDate - aNewestDate; // Descending order (newest categories first)
  });

  return (
    <div className="w-full p-8">
      <header className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-3">
          <img src="/bear.png" alt="Logo" className="w-12 h-12 object-contain" />
          <h1 className="m-0 text-text-dark text-3xl font-light font-serif">
            {siteConfig.title}
          </h1>
        </div>
        <div className="flex items-center gap-4">
          {siteConfig.showVideosButton && (
            <button
              className="p-3 sm:px-6 sm:py-3 bg-blue-600 text-white border-none rounded text-base cursor-pointer transition-colors duration-200 flex items-center justify-center sm:justify-start sm:gap-2 hover:bg-blue-700"
              onClick={handleVideosClick}
              title="Game Videos"
            >
              <span className="material-icons">play_circle</span>
              <span className="hidden sm:inline">Videos</span>
            </button>
          )}
          {isAuthorized && (
            <button 
              className="p-3 sm:px-6 sm:py-3 bg-accent text-text-light border-none rounded text-base cursor-pointer transition-colors duration-200 flex items-center justify-center sm:justify-start sm:gap-2 hover:bg-accent/80"
              onClick={() => setIsModalOpen(true)}
              title="Create Album"
            >
              <span className="material-icons">add</span>
              <span className="hidden sm:inline">Create Album</span>
            </button>
          )}
          <SignInButton onAdminSettings={() => navigate('/admin')} />
        </div>
      </header>

      {albums.length === 0 ? (
        <div className="text-center py-12 text-text-dark">
          <p className="text-lg m-0 font-sans">No albums yet. Create your first album to get started!</p>
        </div>
      ) : (
        <div className="space-y-12">
          {sortedCategories.map(category => (
            <div key={category}>
              <h2 className="text-2xl font-semibold text-text-dark font-serif mb-6 pb-2 border-b border-gray-200">
                {category}
              </h2>
              <AlbumGrid
                albums={groupedAlbums[category]}
                onAlbumClick={(albumId) => navigate(`/album/${albumId}`)}
                onDeleteAlbum={handleDeleteAlbum}
                onEditAlbum={handleEditAlbum}
              />
            </div>
          ))}
        </div>
      )}

      <CreateAlbumModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreateAlbum={handleCreateAlbum}
      />

      <EditAlbumModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingAlbum(null);
        }}
        onUpdateAlbum={handleUpdateAlbum}
        albumData={editingAlbum}
      />

      <PinModal
        isOpen={isPinModalOpen}
        onClose={() => setIsPinModalOpen(false)}
        expectedPin={VIDEO_PIN}
        onPinValidated={handlePinValidated}
        title="Enter PIN"
        description="Enter 6-digit PIN to access videos"
      />
    </div>
  );
};

export default HomePage;